import React, { useState } from 'react';
import api from './api';
import { submitFeedback } from './api';
import michaelImg from './assets/michael.png';
import chrisImg from './assets/chris.png';
import { useAuth } from './AuthContext';

const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/8x2cN50FF1HTdxWb8s4wM1d';

export default function Profile({ setPage }) {
  const { user, logout, isSubscribed, scriptsRemaining, trialDaysRemaining, trialExpired, FREE_SCRIPTS, TRIAL_DAYS } = useAuth();
  const [section, setSection] = useState(null);
  const [activating, setActivating] = useState(false);
  const [activateMsg, setActivateMsg] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showStripeGuide, setShowStripeGuide] = useState(false);
  const [cancelDone, setCancelDone] = useState(false);
  const [showStripeConfirm, setShowStripeConfirm] = useState(false);

  const handleActivate = async () => {
    window.gtag?.('event', 'activate_pro_clicked', { location: 'profile_page' });
    setActivating(true);
    setActivateMsg('');
    try {
      await api.post('/subscription/activate-manual');
      setActivateMsg('success');
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      setActivateMsg('error');
    } finally {
      setActivating(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await api.post('/subscription/cancel');
      setShowCancelConfirm(false);
      setCancelDone(true);
    } catch (e) {
      console.error(e);
    } finally {
      setCancelling(false);
    }
  };

  const handleSubscribe = () => {
    window.gtag?.('event', 'subscribe_clicked', { location: 'profile_page' });
    setShowStripeConfirm(true);
  };

  const handleStripeConfirm = () => {
    setShowStripeConfirm(false);
    const successUrl = encodeURIComponent('https://mytappingscript.com/success');
    window.open(`${STRIPE_PAYMENT_LINK}?prefilled_email=${encodeURIComponent(user?.email || '')}&success_url=${successUrl}`, '_blank');
    setShowStripeGuide(true);
  };

  if (section === 'learnpage') { setPage('learn'); return null; }
  if (section === 'about') return <AboutPage onBack={() => setSection(null)} setPage={setPage} />;
  if (section === 'feature') return <FeatureRequestPage onBack={() => setSection(null)} userEmail={user?.email} />;
  if (section === 'privacy') return <PolicyPage title="Privacy Policy" onBack={() => setSection(null)} content={PRIVACY} />;
  if (section === 'terms') return <PolicyPage title="Terms of Service" onBack={() => setSection(null)} content={TERMS} />;
  if (section === 'disclaimer') return <PolicyPage title="Disclaimer" onBack={() => setSection(null)} content={DISCLAIMER} />;

  return (
    <div className="main-page" style={{ paddingTop: 'env(safe-area-inset-top, 16px)' }}>
      <div className="page-container" style={{ maxWidth: 600, padding: '32px 16px 24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 36, marginBottom: 32 }}>Profile</h1>

        {/* User card */}
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
            
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 17 }}>{user?.name}</div>
            <div style={{ fontSize: 14, color: 'var(--white-60)' }}>{user?.email}</div>
          </div>
        </div>

        {/* Subscription */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: 'var(--white-60)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Subscription</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: isSubscribed ? 'var(--success)' : 'var(--gold)', marginBottom: 16 }}>
            {isSubscribed ? 'Active — Pro Access' : trialExpired ? 'Trial Ended' : 'Free Trial'}
          </div>

          {!isSubscribed && (
            <>
              {/* Trial status */}
              {!trialExpired && (
                <div style={{ marginBottom: 20 }}>
                  <p style={{ fontSize: 15, color: 'var(--white)', fontWeight: 600, marginBottom: 4 }}>
                    7 scripts · 7 days · No credit card needed
                  </p>
                  {(scriptsRemaining < FREE_SCRIPTS || trialDaysRemaining < TRIAL_DAYS) && (
                    <p style={{ fontSize: 13, color: 'var(--white-60)' }}>
                      {scriptsRemaining} script{scriptsRemaining !== 1 ? 's' : ''} and {trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''} remaining
                    </p>
                  )}
                </div>
              )}

              {/* Subscribe button */}
              <button className="btn-primary" onClick={handleSubscribe} style={{ marginBottom: 8, letterSpacing: '0.03em' }}>
                SUBSCRIBE — $5.99/MONTH · LAUNCH PRICE
              </button>
              <p style={{ fontSize: 12, color: 'var(--white-60)', textAlign: 'center', marginBottom: 16 }}>
                Cancel anytime. No lock-in.
              </p>

              {showStripeGuide && (
                <div style={{ background: 'rgba(212,168,67,0.1)', border: '1px solid var(--gold)', borderRadius: 8, padding: '14px', fontSize: 14, color: 'var(--gold)', marginBottom: 12, textAlign: 'center', lineHeight: 1.6 }}>
                  Complete your payment in the Stripe tab, then close it and return here. Click <strong>"I've Already Paid"</strong> below to activate your account.
                </div>
              )}

              <button onClick={handleActivate} disabled={activating}
                style={{
                  width: '100%', padding: '13px', borderRadius: 8, marginBottom: 12,
                  background: 'transparent', border: '1px solid var(--gold)',
                  color: 'var(--gold)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  opacity: activating ? 0.6 : 1,
                }}>
                {activating ? 'Activating...' : "✓ I've Already Paid — Activate Pro"}
              </button>

              {activateMsg === 'success' && (
                <div style={{ background: 'rgba(76,175,125,0.15)', border: '1px solid rgba(76,175,125,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 14, color: '#4caf7d', marginBottom: 12, textAlign: 'center' }}>
                  Pro access activated! Refreshing...
                </div>
              )}
              {activateMsg === 'error' && (
                <div style={{ background: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 14, color: 'var(--error)', marginBottom: 12, textAlign: 'center' }}>
                  No active subscription found. Please complete payment first.
                </div>
              )}
            </>
          )}

          {isSubscribed && (
            <>
              {cancelDone ? (
                <div style={{ background: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 14, color: 'var(--error)', marginBottom: 16, textAlign: 'center' }}>
                  Subscription cancelled — you keep full access until the end of your billing period.
                </div>
              ) : user?.cancel_at_period_end ? (
                <div style={{ background: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 14, color: 'var(--error)', marginBottom: 16, textAlign: 'center' }}>
                  Subscription cancelled — you keep full access until the end of your billing period.
                </div>
              ) : (
                <div style={{ background: 'var(--gold-10)', border: '1px solid var(--gold-20)', borderRadius: 8, padding: '10px 14px', fontSize: 14, color: 'var(--gold)', marginBottom: 16, textAlign: 'center' }}>
                  ✓ Pro access active — unlimited scripts
                </div>
              )}

              {!cancelDone && !user?.cancel_at_period_end && (
                <button onClick={() => setShowCancelConfirm(true)}
                  style={{ background: 'none', border: 'none', color: 'var(--white-60)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', width: '100%', textAlign: 'center', marginBottom: 8 }}>
                  Cancel subscription
                </button>
              )}
            </>
          )}

          {/* Stripe payment confirmation modal */}
          {showStripeConfirm && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
              <div style={{ background: 'var(--navy-card)', borderRadius: '20px 20px 0 0', padding: '32px 24px', paddingBottom: 'max(env(safe-area-inset-bottom, 28px), 28px)', maxWidth: 480, width: '100%', border: '1px solid var(--white-10)' }}>
                {/* Icon */}
                <div style={{ width: 56, height: 56, background: 'rgba(212,168,67,0.15)', border: '1px solid var(--gold-20)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 26 }}>
                  🔒
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 26, marginBottom: 12, textAlign: 'center' }}>Secure Payment via Stripe</h3>
                <p style={{ color: 'var(--white-70)', fontSize: 15, lineHeight: 1.75, marginBottom: 10, textAlign: 'center' }}>
                  Your payment is processed by <strong style={{ color: 'var(--white)' }}>Stripe</strong> — one of the world's most trusted payment platforms. Your card details are never stored by us.
                </p>
                <p style={{ color: 'var(--white-60)', fontSize: 14, lineHeight: 1.7, marginBottom: 24, textAlign: 'center' }}>
                  You'll be taken to a <strong style={{ color: 'var(--white-80)' }}>Stripe payment page</strong> in a new window. Once your payment is complete, return here and tap <strong style={{ color: 'var(--gold)' }}>"I've Already Paid"</strong> to activate your account.
                </p>
                <button className="btn-primary" onClick={handleStripeConfirm} style={{ marginBottom: 12, fontSize: 16 }}>
                  Continue to Payment →
                </button>
                <button onClick={() => setShowStripeConfirm(false)}
                  style={{ width: '100%', padding: '13px', borderRadius: 8, background: 'transparent', border: 'none', color: 'var(--white-60)', fontSize: 14, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Cancel confirmation modal */}
          {showCancelConfirm && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0 }}>
              <div style={{ background: 'var(--navy-card)', border: '1px solid var(--white-10)', borderRadius: '20px 20px 0 0', padding: '28px 20px', paddingBottom: 'max(env(safe-area-inset-bottom, 24px), 24px)', maxWidth: 480, width: '100%', overflowY: 'auto', maxHeight: '90vh' }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 24, marginBottom: 12 }}>Cancel Subscription?</h3>
                <p style={{ color: 'var(--white-60)', fontSize: 15, lineHeight: 1.7, marginBottom: 24 }}>
                  Are you sure you want to cancel? You'll keep full access until the end of your current billing period.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button onClick={handleCancel} disabled={cancelling}
                    style={{ width: '100%', padding: '13px', borderRadius: 8, background: 'transparent', border: '1px solid var(--error)', color: 'var(--error)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                    {cancelling ? 'Cancelling...' : 'Yes, cancel my subscription'}
                  </button>
                  <button onClick={() => setShowCancelConfirm(false)}
                    style={{ width: '100%', padding: '13px', borderRadius: 8, background: 'var(--gold)', border: 'none', color: 'var(--navy)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                    Keep my subscription
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Features */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
            {[
              'Unlimited script creation',
              'All 9 tapping styles included',
              'Audio playback — guided by Sarah AI voice',
              'Download every script as PDF',
              'Save and organise your library',
            ].map(f => (
              <div key={f} className="paywall-feature" style={{ fontSize: 14 }}>
                <span className="paywall-check" style={{ color: 'var(--gold)' }}>✓</span>
                <span style={{ color: isSubscribed ? 'var(--white)' : 'var(--white-60)' }}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent scripts */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: 'var(--white-60)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 14 }}>Recent Scripts</div>
          <button className="btn-gold-outline" onClick={() => setPage('library')}>
            📖 Visit Your Library
          </button>
        </div>

        {/* About */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: 'var(--white-60)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 14 }}>About</div>
          {[
            { label: 'How to Use EFT', key: 'learnpage' },
            { label: 'About Us', key: 'about' },
            { label: 'Request a Feature / Report a Bug', key: 'feature' },
            { label: 'Privacy Policy', key: 'privacy' },
            { label: 'Terms of Service', key: 'terms' },
            { label: 'Disclaimer', key: 'disclaimer' },
          ].map(item => (
            <div key={item.key} onClick={() => setSection(item.key)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 0', borderBottom: '1px solid var(--white-10)', cursor: 'pointer', transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--gold)'}
              onMouseLeave={e => e.currentTarget.style.color = ''}>
              <span style={{ fontSize: 15 }}>⬤ {item.label}</span>
              <span style={{ color: 'var(--white-60)' }}>›</span>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="card" style={{ marginBottom: 24, background: 'var(--navy-mid)' }}>
          <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--white-80)' }}>Disclaimer</p>
          <p style={{ fontSize: 12, color: 'var(--white-60)', lineHeight: 1.7 }}>
            My Tapping Script is a wellness tool designed for self-help and personal growth. It is not a substitute for professional medical advice, diagnosis, or treatment. If you're experiencing severe emotional distress, please seek help from a qualified mental health professional.
          </p>
        </div>

        {/* Sign out */}
        <button onClick={logout}
          style={{ width: '100%', padding: '14px', borderRadius: 10, background: 'transparent', border: '1px solid var(--error)', color: 'var(--error)', fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'background 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(224,92,92,0.1)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          ⇥ SIGN OUT
        </button>

        <p style={{ textAlign: 'center', color: 'var(--white-30)', fontSize: 12, marginTop: 20 }}>My Tapping Script v1.0.0</p>
      </div>
    </div>
  );
}

function AboutPage({ onBack, setPage }) {
  return (
    <div className="main-page" style={{ paddingTop: 'env(safe-area-inset-top, 16px)' }}>
      <div className="page-container" style={{ maxWidth: 700, padding: '32px 16px 24px' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: 15, cursor: 'pointer', marginBottom: 32, display: 'flex', alignItems: 'center', gap: 8 }}>
          ← Back
        </button>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 36, marginBottom: 32 }}>About Us</h1>
        <div className="card" style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <img src={michaelImg} alt="Michael Hetherington"
              style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover', objectPosition: 'center top', border: '3px solid var(--gold)', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 200 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, marginBottom: 4 }}>Michael Hetherington</h2>
              <p style={{ color: 'var(--gold)', fontSize: 14, marginBottom: 16 }}>EFT Practitioner · Chinese Medicine Acupuncturist · Educator</p>
              <p style={{ color: 'var(--white-80)', fontSize: 15, lineHeight: 1.8 }}>
                Michael Hetherington is an EFT Tapping practitioner, teacher, Chinese medicine acupuncturist, author and counsellor. He holds a bachelor's in Chinese medicine, a graduate certificate in counselling, and is completing a master's of education.
              </p>
            </div>
          </div>
        </div>
        <div style={{ color: 'var(--white-80)', fontSize: 15, lineHeight: 1.8, marginBottom: 28 }}>
          <p style={{ marginBottom: 16 }}>He has been working in the field of therapy and healing for over 15 years, helping people release emotional blocks and reclaim their wellbeing through EFT Tapping, acupuncture, and Chinese medicine.</p>
          <p style={{ marginBottom: 16 }}>He designed My Tapping Script because he wanted to help people become more self-empowered in their healing process — giving everyone access to personalised EFT tapping scripts, wherever they are in the world.</p>
        </div>

        <div className="card" style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <img src={chrisImg} alt="Chris Young"
              style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover', objectPosition: 'center top', border: '3px solid var(--gold)', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 200 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, marginBottom: 4 }}>Chris Young</h2>
              <p style={{ color: 'var(--gold)', fontSize: 14, marginBottom: 16 }}>Tech Developer</p>
              <p style={{ color: 'var(--white-80)', fontSize: 15, lineHeight: 1.8 }}>
                Chris Young is a tech developer and digital nomad originally from the UK, now based in South East Asia. With over 7 years of experience in the tech industry, he has worked on major apps in the health and wellness space. Chris brings the technical vision behind My Tapping Script, building the platform that makes personalised EFT accessible to everyone.
              </p>
            </div>
          </div>
        </div>
        <div style={{ borderTop: '1px solid var(--white-10)', paddingTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <a href="https://www.michaelhetherington.com" target="_blank" rel="noreferrer">
            <button className="btn-primary" style={{ fontSize: 15 }}>🌐 Visit michaelhetherington.com</button>
          </a>
          <button className="btn-secondary" onClick={() => setPage('learn')} style={{ fontSize: 15 }}>Learn EFT Tapping</button>
        </div>
      </div>
    </div>
  );
}

function FeatureRequestPage({ onBack, userEmail }) {
  const [type, setType] = useState('feature');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async () => {
    if (!message.trim()) return;
    setLoading(true);
    setError('');
    try {
      await submitFeedback({ user_email: userEmail || 'Unknown', request_text: message, type });
      setSent(true);
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-page" style={{ paddingTop: 'env(safe-area-inset-top, 16px)' }}>
      <div className="page-container" style={{ maxWidth: 600, padding: '32px 16px 24px' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: 15, cursor: 'pointer', marginBottom: 32, display: 'flex', alignItems: 'center', gap: 8 }}>
          ← Back
        </button>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 36, marginBottom: 8 }}>Request a Feature / Report a Bug</h1>
        <p style={{ color: 'var(--white-60)', fontSize: 15, marginBottom: 32 }}>We read every submission. Thank you for helping improve the app.</p>

        {sent ? (
          <div style={{ background: 'rgba(76,175,125,0.1)', border: '1px solid rgba(76,175,125,0.3)', borderRadius: 12, padding: 28, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 24, marginBottom: 8 }}>Thank you!</h3>
            <p style={{ color: 'var(--white-60)', fontSize: 15 }}>Your message has been sent directly to the team.</p>
            <button className="btn-secondary" onClick={onBack} style={{ marginTop: 20 }}>← Back to Profile</button>
          </div>
        ) : (
          <>
            {/* Type toggle */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 24, background: 'var(--white-10)', borderRadius: 8, padding: 4 }}>
              {[{ id: 'feature', label: '💡 Feature Request' }, { id: 'bug', label: '🐛 Bug Report' }].map(t => (
                <button key={t.id} onClick={() => setType(t.id)} style={{
                  flex: 1, padding: '10px', borderRadius: 6, fontSize: 14, fontWeight: 600,
                  background: type === t.id ? 'var(--gold)' : 'transparent',
                  color: type === t.id ? 'var(--navy)' : 'var(--white-60)',
                  border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                }}>
                  {t.label}
                </button>
              ))}
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 15, fontWeight: 500, display: 'block', marginBottom: 10 }}>
                {type === 'bug' ? 'Describe the bug — what happened and when?' : 'What would you like to see in the app?'}
              </label>
              <textarea
                placeholder={type === 'bug' ? 'e.g. The audio stopped working after I pressed pause...' : 'e.g. I would love to be able to...'}
                value={message} onChange={e => setMessage(e.target.value)} rows={6} style={{ resize: 'vertical' }} />
            </div>

            {error && <p style={{ color: 'var(--error)', fontSize: 14, marginBottom: 12 }}>{error}</p>}

            <button className="btn-primary" onClick={handleSend} disabled={!message.trim() || loading}>
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function PolicyPage({ title, onBack, content }) {
  return (
    <div className="main-page" style={{ paddingTop: 'env(safe-area-inset-top, 16px)' }}>
      <div className="page-container" style={{ maxWidth: 700, padding: '32px 16px 24px' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: 15, cursor: 'pointer', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
          ← Back
        </button>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 36, marginBottom: 24 }}>{title}</h1>
        <div style={{ color: 'var(--white-60)', lineHeight: 1.8, fontSize: 15 }} dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    </div>
  );
}

const PRIVACY = `
<p style="color:var(--white-40);font-size:13px;margin-bottom:24px">Last updated: April 2026</p>

<p><strong style="color:white">1. Introduction</strong></p>
<br/>
<p>My Tapping Script ("the App", "we", "us", "our") is committed to protecting your privacy.</p>
<br/>
<p>This Privacy Policy explains how we collect, use, store, and protect your personal information when you use the App.</p>
<br/>
<p>By using the App, you agree to the collection and use of information as described in this policy.</p>
<br/>

<p><strong style="color:white">2. Information We Collect</strong></p>
<br/>
<p>We collect only the information necessary to operate the App.</p>
<br/>
<p><strong style="color:var(--white-80)">a) Account Information</strong></p>
<br/>
<p>When you create an account, we may collect:</p>
<ul style="padding-left:20px;margin:8px 0">
  <li style="margin-bottom:4px">Email address</li>
  <li style="margin-bottom:4px">Account ID</li>
  <li style="margin-bottom:4px">Subscription status</li>
  <li style="margin-bottom:4px">Basic profile information (if provided)</li>
</ul>
<br/>
<p><strong style="color:var(--white-80)">b) App Usage Information</strong></p>
<br/>
<p>We may collect:</p>
<ul style="padding-left:20px;margin:8px 0">
  <li style="margin-bottom:4px">EFT script inputs (e.g. topics, emotions, preferences)</li>
  <li style="margin-bottom:4px">Generated scripts saved to your account</li>
  <li style="margin-bottom:4px">App interaction data (feature usage, session activity)</li>
</ul>
<br/>
<p>This information is used only to provide and improve the service.</p>
<br/>
<p><strong style="color:var(--white-80)">c) Payment Information</strong></p>
<br/>
<p>Payments are handled by a third-party payment provider (e.g. Stripe).</p>
<ul style="padding-left:20px;margin:8px 0">
  <li style="margin-bottom:4px">We do not store your credit card details</li>
  <li style="margin-bottom:4px">We only store confirmation of payment status and subscription plan</li>
</ul>
<br/>

<p><strong style="color:white">3. How We Use Your Information</strong></p>
<br/>
<p>We use your information to:</p>
<ul style="padding-left:20px;margin:8px 0">
  <li style="margin-bottom:4px">Create and manage your account</li>
  <li style="margin-bottom:4px">Provide personalized EFT scripts</li>
  <li style="margin-bottom:4px">Manage subscriptions and billing</li>
  <li style="margin-bottom:4px">Improve app performance and reliability</li>
  <li style="margin-bottom:4px">Communicate important account or service updates</li>
</ul>
<br/>
<p>We do not use your data for advertising or profiling.</p>
<br/>

<p><strong style="color:white">4. No Selling or Sharing of Data</strong></p>
<br/>
<p>We take a strict approach to user privacy.</p>
<ul style="padding-left:20px;margin:8px 0">
  <li style="margin-bottom:4px">We do not sell your personal information</li>
  <li style="margin-bottom:4px">We do not share your personal data with third parties</li>
  <li style="margin-bottom:4px">We do not allow third parties to use your data for marketing</li>
</ul>
<br/>
<p>Your information stays private and is used only to operate the App.</p>
<br/>

<p><strong style="color:white">5. Data Storage &amp; Security</strong></p>
<br/>
<ul style="padding-left:20px;margin:8px 0">
  <li style="margin-bottom:4px">User data is stored securely using industry-standard systems</li>
  <li style="margin-bottom:4px">We take reasonable technical and organizational measures to protect data</li>
  <li style="margin-bottom:4px">Access to user data is restricted and monitored</li>
</ul>
<br/>
<p>While no system can guarantee 100% security, we make every reasonable effort to protect your information.</p>
<br/>

<p><strong style="color:white">6. User Control &amp; Access</strong></p>
<br/>
<p>You may:</p>
<ul style="padding-left:20px;margin:8px 0">
  <li style="margin-bottom:4px">View and update your account information</li>
  <li style="margin-bottom:4px">Cancel your subscription at any time via the Profile section</li>
  <li style="margin-bottom:4px">Request deletion of your account and associated data</li>
</ul>
<br/>
<p>To request data access or deletion, please contact us using the details below.</p>
<br/>

<p><strong style="color:white">7. Data Retention</strong></p>
<br/>
<p>We retain your data only for as long as:</p>
<ul style="padding-left:20px;margin:8px 0">
  <li style="margin-bottom:4px">Your account remains active, or</li>
  <li style="margin-bottom:4px">It is required to provide the service, or</li>
  <li style="margin-bottom:4px">We are legally required to retain it</li>
</ul>
<br/>
<p>When accounts are deleted, associated data is removed within a reasonable timeframe, unless required by law.</p>
<br/>

<p><strong style="color:white">8. Cookies &amp; Tracking</strong></p>
<br/>
<p>The App may use essential cookies or similar technologies to:</p>
<ul style="padding-left:20px;margin:8px 0">
  <li style="margin-bottom:4px">Maintain login sessions</li>
  <li style="margin-bottom:4px">Ensure basic functionality</li>
</ul>
<br/>
<p>We do not use tracking cookies for advertising or behavioral profiling.</p>
<br/>

<p><strong style="color:white">9. Children's Privacy</strong></p>
<br/>
<p>The App is not intended for use by children under the age of 18.</p>
<br/>
<p>We do not knowingly collect personal information from minors.</p>
<br/>

<p><strong style="color:white">10. International Users</strong></p>
<br/>
<p>If you access the App from outside your country of residence, you understand that your data may be processed in jurisdictions with different data protection laws.</p>
<br/>

<p><strong style="color:white">11. Changes to This Policy</strong></p>
<br/>
<p>We may update this Privacy Policy from time to time.</p>
<ul style="padding-left:20px;margin:8px 0">
  <li style="margin-bottom:4px">Updates will be reflected in the App</li>
  <li style="margin-bottom:4px">Continued use of the App means you accept the updated policy</li>
</ul>
<br/>

<p><strong style="color:white">12. Contact Information</strong></p>
<br/>
<p>If you have any questions or concerns about this Privacy Policy, please contact:</p>
<br/>
<p><strong style="color:var(--white-80)">My Tapping Script</strong></p>
<p>Email: <a href="mailto:info@michaelhetherington.com" style="color:var(--gold)">info@michaelhetherington.com</a></p>
`;

const TERMS = `
<p style="color:var(--white-40);font-size:13px;margin-bottom:24px">Last updated: April 2026</p>

<p><strong style="color:white">1. Acceptance of Terms</strong></p>
<br/>
<p>By downloading, accessing, or using My Tapping Script ("the App"), you agree to be bound by these Terms of Service. If you do not agree, do not use the App.</p>
<br/>

<p><strong style="color:white">2. About the App</strong></p>
<br/>
<p>My Tapping Script provides personalised EFT (Emotional Freedom Techniques) tapping scripts for wellness and self-help purposes only. The App is operated by Michael Hetherington (<a href="mailto:info@michaelhetherington.com" style="color:var(--gold)">info@michaelhetherington.com</a>).</p>
<br/>

<p><strong style="color:white">3. Not Medical or Psychological Advice</strong></p>
<br/>
<p>The content generated by this App is for general wellness and self-help purposes only. It is not medical advice, psychological treatment, or a substitute for professional mental health care. If you are experiencing a mental health crisis, please contact a qualified health professional or emergency services. By using the App you acknowledge that EFT tapping scripts are self-help tools and not clinical treatment.</p>
<br/>

<p><strong style="color:white">4. Eligibility</strong></p>
<br/>
<p>You must be 18 years or older to use this App. By using the App you confirm you meet this requirement.</p>
<br/>

<p><strong style="color:white">5. Account Registration</strong></p>
<br/>
<p>You are responsible for maintaining the confidentiality of your account credentials. You agree to provide accurate information when creating your account. We reserve the right to suspend or terminate accounts that violate these terms.</p>
<br/>

<p><strong style="color:white">6. Subscription and Billing</strong></p>
<br/>
<ul style="padding-left:20px;margin:8px 0">
  <li style="margin-bottom:6px">Free users receive 5 scripts at no cost</li>
  <li style="margin-bottom:6px">Paid subscribers receive unlimited scripts at $5.99 USD per month, billed monthly</li>
  <li style="margin-bottom:6px">Subscriptions automatically renew unless cancelled</li>
  <li style="margin-bottom:6px">You may cancel at any time via the Profile section — cancellation takes effect at the end of the current billing period</li>
  <li style="margin-bottom:6px">We reserve the right to adjust pricing with reasonable notice — existing subscribers will be notified before any price change affects their account</li>
  <li style="margin-bottom:6px">Payments are processed securely by Stripe — we do not store your payment card details</li>
</ul>
<br/>

<p><strong style="color:white">7. Refunds</strong></p>
<br/>
<p>Due to the nature of digital content, we do not offer refunds for partially used subscription periods. If you experience a technical issue preventing access to the service, contact us at <a href="mailto:info@michaelhetherington.com" style="color:var(--gold)">info@michaelhetherington.com</a> and we will work to resolve it.</p>
<br/>

<p><strong style="color:white">8. Acceptable Use</strong></p>
<br/>
<p>You agree not to:</p>
<ul style="padding-left:20px;margin:8px 0">
  <li style="margin-bottom:6px">Use the App for any unlawful purpose</li>
  <li style="margin-bottom:6px">Attempt to reverse-engineer, copy, or reproduce the App or its content</li>
  <li style="margin-bottom:6px">Share your account with others</li>
  <li style="margin-bottom:6px">Use the App to generate content intended to harm yourself or others</li>
  <li style="margin-bottom:6px">Misrepresent your identity or provide false information</li>
</ul>
<br/>

<p><strong style="color:white">9. Intellectual Property</strong></p>
<br/>
<p>All content, design, and functionality of the App is the property of Michael Hetherington unless otherwise stated. Generated scripts are provided for your personal use only and may not be resold or distributed commercially.</p>
<br/>

<p><strong style="color:white">10. Limitation of Liability</strong></p>
<br/>
<p>To the maximum extent permitted by law, My Tapping Script and its operator shall not be liable for any direct, indirect, incidental, or consequential damages arising from your use of or inability to use the App. The App is provided "as is" without warranties of any kind.</p>
<br/>

<p><strong style="color:white">11. Indemnification</strong></p>
<br/>
<p>You agree to indemnify and hold harmless My Tapping Script and its operator from any claims, damages, or expenses arising from your misuse of the App or violation of these Terms.</p>
<br/>

<p><strong style="color:white">12. Changes to Terms</strong></p>
<br/>
<p>We may update these Terms from time to time. Continued use of the App after changes are posted constitutes acceptance of the updated Terms. We will notify users of significant changes via email or in-app notice.</p>
<br/>

<p><strong style="color:white">13. Governing Law</strong></p>
<br/>
<p>These Terms are governed by the laws of Queensland, Australia. Any disputes shall be subject to the jurisdiction of Queensland courts.</p>
<br/>

<p><strong style="color:white">14. Contact</strong></p>
<br/>
<p>For any questions regarding these Terms please contact:</p>
<br/>
<p><strong style="color:var(--white-80)">My Tapping Script</strong></p>
<p>Email: <a href="mailto:info@michaelhetherington.com" style="color:var(--gold)">info@michaelhetherington.com</a></p>
`;

const DISCLAIMER = `
<p style="color:var(--white-40);font-size:13px;margin-bottom:24px">Last updated: April 2026</p>

<p><strong style="color:white">1. General</strong></p>
<br/>
<p>My Tapping Script is a self-help wellness tool designed to support emotional wellbeing through EFT (Emotional Freedom Techniques) tapping scripts. By using this App you acknowledge and agree to the terms of this Disclaimer.</p>
<br/>

<p><strong style="color:white">2. Not a Medical or Mental Health Service</strong></p>
<br/>
<p>The App and all content it generates is for general self-help and wellness purposes only. Nothing in this App constitutes or should be interpreted as:</p>
<ul style="padding-left:20px;margin:8px 0">
  <li style="margin-bottom:6px">Medical advice or diagnosis</li>
  <li style="margin-bottom:6px">Psychological or psychiatric treatment</li>
  <li style="margin-bottom:6px">Counselling or therapy</li>
  <li style="margin-bottom:6px">A substitute for professional healthcare</li>
</ul>
<br/>
<p>Always consult a qualified medical or mental health professional for any physical or psychological health concerns.</p>
<br/>

<p><strong style="color:white">3. EFT Tapping — General Information</strong></p>
<br/>
<p>EFT (Emotional Freedom Techniques) is a self-help practice used by many people for stress relief and emotional regulation. While EFT is widely used and studied, individual results vary. We make no guarantees regarding specific outcomes from using the scripts generated by this App.</p>
<br/>

<p><strong style="color:white">4. Personal Responsibility</strong></p>
<br/>
<p>You use this App at your own risk. You are solely responsible for how you apply the content generated. If you experience distress or discomfort while using any tapping script, stop immediately and seek support from a qualified professional.</p>
<br/>

<p><strong style="color:white">5. Crisis Support</strong></p>
<br/>
<p>This App is not designed for use in a mental health crisis. If you are in crisis or at risk of harming yourself or others, please contact:</p>
<ul style="padding-left:20px;margin:8px 0">
  <li style="margin-bottom:6px"><strong style="color:var(--white-80)">Australia:</strong> Lifeline 13 11 14</li>
  <li style="margin-bottom:6px"><strong style="color:var(--white-80)">USA:</strong> 988 Suicide and Crisis Lifeline — call or text 988</li>
  <li style="margin-bottom:6px"><strong style="color:var(--white-80)">UK:</strong> Samaritans 116 123</li>
  <li style="margin-bottom:6px"><strong style="color:var(--white-80)">International:</strong> <a href="https://findahelpline.com" target="_blank" rel="noreferrer" style="color:var(--gold)">findahelpline.com</a></li>
</ul>
<br/>

<p><strong style="color:white">6. Accuracy of Generated Content</strong></p>
<br/>
<p>Scripts are generated using AI technology based on your inputs. While every effort is made to ensure quality and relevance, we cannot guarantee that every script will be suitable for every individual or situation.</p>
<br/>

<p><strong style="color:white">7. Third Party Services</strong></p>
<br/>
<p>The App uses third party services including Stripe for payments and email delivery services. We are not responsible for the practices or content of these third party providers.</p>
<br/>

<p><strong style="color:white">8. Limitation of Liability</strong></p>
<br/>
<p>To the fullest extent permitted by law, My Tapping Script and its operator accept no liability for any harm, loss, or damage arising directly or indirectly from use of the App or its generated content.</p>
<br/>

<p><strong style="color:white">9. Contact</strong></p>
<br/>
<p>For questions regarding this Disclaimer please contact:</p>
<br/>
<p><strong style="color:var(--white-80)">My Tapping Script</strong></p>
<p>Email: <a href="mailto:info@michaelhetherington.com" style="color:var(--gold)">info@michaelhetherington.com</a></p>
`;

export { PRIVACY, TERMS, DISCLAIMER };
